import { useDisclosure } from '@/hooks';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Popover,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType, DeleteIcon, Group, EditIcon } from '@/components/_common';
import { useRouteMatch } from 'react-router-dom';
import {
  useCorrections,
  useCreateCorrection,
  useDeleteCorrection,
  useUpdateCorrection,
} from '@/core/domain/invoice/Correction/Correction.hooks';
import {
  CorrectionSituationColor,
  CorrectionSituationLabel,
  CorrectionsRequest,
  CorrectionsResponse,
  SituationCorrections,
} from '@/core/domain/invoice/Correction/Correction.types';
import moment from 'moment';
import {
  useGeneratePdfCorrection,
  useTransmissionCorrection,
} from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import StatusTag from '@/components/_common/StatusTag';
import ActionIcon from '@/components/_common/ActionIcon';
import { ReactElement, useState } from 'react';
import { Eye } from 'react-feather';
import CceForm from './CceForm';
import { DateTimeFormat } from '@/utils/moment';

const InvoiceCceForm: React.FC = () => {
  const iconFileStyle = {
    fontWeight: 900,
    fontSize: 15,
    letterSpacing: '-1px',
  };

  const { Title } = Typography;

  const report = useReportGenerate();

  const [form] = Form.useForm<{ description: string }>();

  const [modalVisible, setModalVisible] = useDisclosure(false);
  const [cceSelectedId, setCceSelectedId] = useState(undefined);

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const createCorrection = useCreateCorrection();
  const updateCorrection = useUpdateCorrection();
  const deleteCorrection = useDeleteCorrection();
  const transmissionCorrection = useTransmissionCorrection();
  const generatePdfCorrection = useGeneratePdfCorrection();

  const isLoadingActions =
    updateCorrection.isLoading ||
    createCorrection.isLoading ||
    deleteCorrection.isLoading ||
    generatePdfCorrection.isLoading ||
    transmissionCorrection.isLoading;

  const { data, isLoading } = useCorrections(Number(invoiceId));

  const disabledActionCreate =
    !data?.every((cce) => cce.status === SituationCorrections.AUTHORIZED) ||
    data.length >= 20;

  const columns: ColumnsType<CorrectionsResponse> = [
    {
      dataIndex: 'sequenceNumber',
      title: 'Nº sequência',
      render: (value) => (value ? value : '-'),
    },
    {
      dataIndex: 'eventDateTime',
      title: 'Data de criação',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
    },
    {
      dataIndex: 'status',
      title: 'Situação',
      render: (status: SituationCorrections, row) => {
        if (status) {
          return (
            <Tooltip
              placement="topLeft"
              title={status === SituationCorrections.REJECTED ? row.reason : ''}
            >
              <StatusTag
                color={CorrectionSituationColor[status]}
                label={CorrectionSituationLabel[status]}
              />
            </Tooltip>
          );
        }
        return '-';
      },
    },
    {
      dataIndex: 'authorizationProtocol',
      title: 'Nº do protocolo',
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Impressão',
      width: 150,
      align: 'center',
      render: (row) => {
        const isAuthorized = row.status === SituationCorrections.AUTHORIZED;

        const renderIcon = (icon: ReactElement) => (
          <Popover content={!isAuthorized ? 'Sem arquivo' : undefined}>
            <div>{icon}</div>
          </Popover>
        );

        return (
          <Group position="center" align="center" gap={10}>
            {renderIcon(
              <ActionIcon
                onClick={() =>
                  handleGenerateReport(row.id, (result) =>
                    report.downloadFile({
                      result,
                      fileName: `Carta-de-correção-${row.id}.pdf`,
                    }),
                  )
                }
                disabled={!isAuthorized || isLoadingActions}
              >
                <span style={iconFileStyle}>PDF</span>
              </ActionIcon>,
            )}

            {renderIcon(
              <ActionIcon
                onClick={() =>
                  handleGenerateReport(row.id, (result) =>
                    report.openFile({
                      result,
                      fileName: `Carta-de-correção-${row.id}.pdf`,
                      fileType: 'application/pdf',
                    }),
                  )
                }
                disabled={!isAuthorized || isLoadingActions}
              >
                <Eye size={20} />
              </ActionIcon>,
            )}
          </Group>
        );
      },
    },
    {
      title: 'Ações',
      key: 'action',
      align: 'right',
      render: (row) => (
        <Space>
          {row.status !== SituationCorrections.AUTHORIZED && (
            <>
              <Button
                type="link"
                onClick={() => transmissionCorrection.mutateAsync(row.id)}
                loading={transmissionCorrection.isLoading}
                disabled={isLoadingActions}
              >
                Transmitir
              </Button>

              <Divider />

              <DeleteIcon
                onClick={() => onDelete(row.id)}
                disabled={isLoadingActions}
              />

              <Divider />

              <EditIcon
                disabled={isLoadingActions}
                iconProps={{
                  onClick: () => {
                    form.setFieldValue('description', row.description);
                    setCceSelectedId(row.id);
                    setModalVisible.toggle();
                  },
                }}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  const onOk = async () => {
    const formValues = form.getFieldsValue();

    try {
      const obj: CorrectionsRequest = {
        ...formValues,
        invoiceId: Number(invoiceId),
        eventDateTime: moment().format(DateTimeFormat),
      };

      if (cceSelectedId) {
        await updateCorrection.mutateAsync({
          ...obj,
          invoiceId: Number(invoiceId),
          cceId: cceSelectedId,
        });
        setCceSelectedId(undefined);
      } else {
        await createCorrection.mutateAsync({
          ...obj,
          invoiceId: Number(invoiceId),
        });
      }
      form.setFieldValue('description', undefined);
      setModalVisible.toggle();
    } catch (error) {}
  };

  const onCancel = async () => {
    form.setFieldValue('description', undefined);
    setCceSelectedId(undefined);
    setModalVisible.toggle();
  };

  const onDelete = async (id: number) => {
    Modal.confirm({
      title: 'Deseja realmente excluir a Carta de Correção?',
      content: `Ao remover a Carta de Correção, a recuperação da mesma não poderá ser feita. Ainda sim deseja continuar?`,
      onOk: () => {
        deleteCorrection.mutateAsync({
          invoiceId: Number(invoiceId),
          cceId: Number(id),
        });
      },
      okType: 'primary',
    });
  };

  const handleGenerateReport = async (
    cceId: number,
    callback?: ReportCallback,
  ) => {
    try {
      const result = await generatePdfCorrection.mutateAsync(cceId);

      if (callback) {
        callback(result);
      }
    } catch {}
  };

  return (
    <Form form={form} layout="vertical">
      <Title level={4}>Cartas de correção</Title>
      <Row gutter={16} justify="end">
        {(data || []).length >= 20 && (
          <Col span={24}>
            <Alert
              type="warning"
              message="Limite de emissão de CC-e atingido."
              description="A quantidade máximo de CC-e por nota é de 20."
              style={{ marginBottom: 24 }}
            />
          </Col>
        )}

        <Col>
          <Button
            type="primary"
            disabled={disabledActionCreate}
            onClick={() => setModalVisible.toggle()}
          >
            Nova CC-e
          </Button>
        </Col>

        <Col span={24}>
          <Table<CorrectionsResponse>
            columns={columns}
            dataSource={data}
            pagination={false}
            loading={isLoading}
          />
        </Col>

        <Modal
          onCancel={onCancel}
          onOk={onOk}
          open={modalVisible}
          title="Carta de correção"
          destroyOnClose
          width={'70%'}
          confirmLoading={createCorrection.isLoading}
        >
          <CceForm form={form} />
        </Modal>
      </Row>
    </Form>
  );
};

export default InvoiceCceForm;
